import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { colors, constants } from "utils";
import { useSchoolReportScope } from "./useSchoolReportScope";
import { DEFAULT_DATE_MODE, resolveSelectedRange } from "./DateRangeSelect";
import { SchoolFiltersBar } from "./SchoolFiltersBar";
import { useLandingPageView, useViewedPageTracking } from "hooks/usePageViewTracker";
import {
  DosageTrendTab,
  StudentCollabTrendTab,
  StudentProficiencyTab,
  TeacherSummaryTab,
} from "./reportTabs";
import { pageTitleInset } from "components/pageTitleInset";

// School and district admins share one page_name; report_scope tells them apart.
const SCHOOL_REPORTS_PAGE_NAME = "School Reports";
const reportScopeOf = (role) => (role === "district_admin" ? "District" : "School");

// Keys are the server report slugs. Summary is the only non-periodic report.
const TABS = [
  { key: "teacher-summary", label: "Summary", Component: TeacherSummaryTab, periodic: false },
  { key: "dosage-trend", label: "Dosage", Component: DosageTrendTab, periodic: true },
  {
    key: "student-collab-trend",
    label: "Collaboration",
    Component: StudentCollabTrendTab,
    periodic: true,
  },
  {
    key: "student-proficiency",
    label: "Proficiency",
    Component: StudentProficiencyTab,
    periodic: true,
  },
];

export const SchoolReports = () => {
  const { scope, loading } = useSchoolReportScope();
  const [schoolId, setSchoolId] = useState(null);
  const [teacherId, setTeacherId] = useState(null);
  const [dateMode, setDateMode] = useState(DEFAULT_DATE_MODE);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [viewBy, setViewBy] = useState("teacher");
  const [trendBy, setTrendBy] = useState("week");
  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const { trackView } = useViewedPageTracking();
  useLandingPageView(
    SCHOOL_REPORTS_PAGE_NAME,
    { tab: TABS[0].label, report_scope: reportScopeOf(scope?.role) },
    { ready: Boolean(scope) }
  );
  // Reports are slow, so nothing loads until the admin asks: a filter or tab
  // change waits for the next Get Report click.
  const [applied, setApplied] = useState(null);

  const { start: startDate, end: endDate } = resolveSelectedRange({
    mode: dateMode,
    customStart,
    customEnd,
  });

  const active = TABS.find((tab) => tab.key === activeTab);

  // Null while the range is incomplete (custom mode mid-edit) so tabs don't
  // fire half-formed requests. view_by and trend_by are always sent so every
  // report comes back in the grouped shape with a `period_of` column.
  const params = useMemo(() => {
    if (!scope || !startDate || !endDate) return null;
    if (new Date(startDate) > new Date(endDate)) return null;
    return {
      start_date: startDate,
      end_date: endDate,
      school_id: schoolId ?? undefined,
      teacher_id: teacherId ?? undefined,
      view_by: viewBy,
      trend_by: active?.periodic ? trendBy : undefined,
    };
  }, [scope, startDate, endDate, schoolId, teacherId, viewBy, trendBy, active]);

  if (loading) return null;
  if (!scope) return <Empty>You don't have access to these reports.</Empty>;

  const ActiveTab = active?.Component;
  const activeTabLabel = active?.label;
  const reportScope = reportScopeOf(scope.role);

  return (
    <Container>
      <PageHeader>
        <HeaderContent>
          <Title>
            {scope.role === "district_admin"
              ? constants.DISTRICT_REPORTS
              : constants.SCHOOL_REPORTS}
          </Title>
        </HeaderContent>
      </PageHeader>

      <ContentArea>
        <SchoolFiltersBar
          scope={scope}
          schoolId={schoolId}
          teacherId={teacherId}
          dateMode={dateMode}
          customStart={customStart}
          customEnd={customEnd}
          startDate={startDate}
          endDate={endDate}
          viewBy={viewBy}
          trendBy={trendBy}
          showTrendBy={Boolean(active?.periodic)}
          onSchoolChange={setSchoolId}
          onTeacherChange={setTeacherId}
          onDateModeChange={setDateMode}
          onCustomStartChange={setCustomStart}
          onCustomEndChange={setCustomEnd}
          onViewByChange={setViewBy}
          onTrendByChange={setTrendBy}
          canGetReport={Boolean(params)}
          onGetReport={() => setApplied({ tab: activeTab, params })}
        />

        <PageTabsBar role="tablist" aria-label="School report views">
          {TABS.map((tab) => (
            <PageTab
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              $active={activeTab === tab.key}
              onClick={() => {
                if (tab.key !== activeTab) {
                  trackView(SCHOOL_REPORTS_PAGE_NAME, {
                    tab: tab.label,
                    report_scope: reportScope,
                  });
                }
                setActiveTab(tab.key);
              }}
            >
              {tab.label}
            </PageTab>
          ))}
        </PageTabsBar>

        {ActiveTab && applied?.tab === activeTab ? (
          <ActiveTab
            params={applied.params}
            tabName={activeTabLabel}
            reportScope={reportScope}
            viewBy={applied.params.view_by}
            trendBy={applied.params.trend_by}
          />
        ) : (
          <Empty>Choose your filters, then select Get Report.</Empty>
        )}
      </ContentArea>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 92px);
  background-color: ${colors.milkGlass200};
`;

const PageHeader = styled.div`
  background-color: ${colors.nightlyWoods500};
  padding: 24px 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px 0 ${pageTitleInset};
`;

const Title = styled.h1`
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 44px;
  color: ${colors.milkGlass500};
  margin: 0;
`;

const ContentArea = styled.div`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PageTabsBar = styled.div`
  display: flex;
  border-bottom: 1px solid #acafad;
`;

const PageTab = styled.button`
  background: none;
  border: none;
  border-bottom: 3px solid ${({ $active }) => ($active ? "#605DEC" : "transparent")};
  margin-bottom: -1px;
  padding: 16px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${({ $active }) => ($active ? "#524FC9" : "#002E19")};
  cursor: pointer;

  &:hover {
    color: #524fc9;
  }
`;

const Empty = styled.div`
  color: ${colors.nightlyWoods700 || "#6B7280"};
  padding: 48px;
  text-align: center;
`;
